import { useState } from 'react';
import { cccdAdmin, type CccdTeamStatus } from '@/lib/api/cccdAdminClient';
import { textareaCls } from '@/components/admin/FormComponents';
import Alert from '@/components/ui/alert/Alert';
import { toastError } from '@/lib/toast';

// cccd-team-status-free-selector: el backend ya acepta cualquier estado
// destino (solo bloquea salir de `rejected`, ver PATCH /teams/:id/status),
// así que la UI ya no impone una whitelist de "próximos estados válidos"
// — listamos todos menos el actual, y usamos este orden solo para decidir
// qué mostrar primero y cuándo pedir confirmación (ver isBackwardOrTerminal
// más abajo), nunca para restringir opciones.
const ALL_STATUSES: CccdTeamStatus[] = ['draft', 'negotiating', 'payment_pending', 'active', 'inactive', 'rejected'];
const PROGRESS_ORDER: CccdTeamStatus[] = ['draft', 'negotiating', 'payment_pending', 'active', 'inactive'];

function progressIndex(status: CccdTeamStatus): number {
  const i = PROGRESS_ORDER.indexOf(status);
  return i === -1 ? PROGRESS_ORDER.length : i; // rejected siempre al final
}

// Transición que retrocede el flujo habitual, o hacia `rejected` — pide
// confirmación explícita porque ya no hay una lista reducida de opciones
// que guíe al admin (cccd-team-status-manual-transitions).
function isBackwardOrTerminal(current: CccdTeamStatus, target: CccdTeamStatus): boolean {
  if (target === 'rejected') return true;
  return progressIndex(target) < progressIndex(current);
}

const LABELS: Record<CccdTeamStatus, string> = {
  draft:           'Volver a borrador',
  negotiating:     'Iniciar negociación',
  payment_pending: 'Marcar pago pendiente',
  active:          'Activar',
  inactive:        'Inactivar',
  rejected:        'Rechazar',
};

export function TeamQuickActions({
  teamId,
  currentStatus,
  onStatusChanged,
}: {
  teamId: string;
  currentStatus: CccdTeamStatus;
  onStatusChanged: (newStatus: CccdTeamStatus) => void;
}) {
  const [note, setNote] = useState('');
  const [rejectedReason, setRejectedReason] = useState('');
  const [loading, setLoading] = useState<CccdTeamStatus | null>(null);
  const [error, setError] = useState(false);
  const [pendingConfirm, setPendingConfirm] = useState<CccdTeamStatus | null>(null);

  // rejected es terminal (el backend rechaza con 409 cualquier intento de
  // salir de ahí) — sin opciones, igual que antes de este change.
  if (currentStatus === 'rejected') return null;

  const nextStatuses = [...ALL_STATUSES]
    .filter((status) => status !== currentStatus)
    .sort((a, b) => {
      const aBackward = isBackwardOrTerminal(currentStatus, a) ? 1 : 0;
      const bBackward = isBackwardOrTerminal(currentStatus, b) ? 1 : 0;
      if (aBackward !== bBackward) return aBackward - bBackward;
      return progressIndex(a) - progressIndex(b);
    });

  const handleChange = async (status: CccdTeamStatus) => {
    setLoading(status);
    setError(false);
    try {
      await cccdAdmin.changeStatus(teamId, { status, note: note || undefined, rejectedReason: rejectedReason || undefined });
      onStatusChanged(status);
      setNote('');
      setRejectedReason('');
    } catch (err: any) {
      setError(true);
      toastError(err?.response?.data?.error ?? 'No se pudo cambiar el estado. Intenta de nuevo.');
    } finally {
      setLoading(null);
    }
  };

  const handleClickStatus = (status: CccdTeamStatus) => {
    if (isBackwardOrTerminal(currentStatus, status)) {
      setPendingConfirm(status);
      return;
    }
    handleChange(status);
  };

  return (
    <div className="space-y-3">
      {error && (
        <Alert variant="error" title="No se pudo cambiar el estado" message="Intenta de nuevo." />
      )}
      <textarea
        className={textareaCls}
        placeholder="Nota interna (opcional)"
        rows={2}
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
      {nextStatuses.includes('rejected') && (
        <input
          className="w-full rounded-xl border border-error-300 bg-white px-4 py-2.5 text-sm text-gray-800 focus:border-error-500 focus:outline-none focus:ring-2 focus:ring-error-500/20 dark:border-error-800 dark:bg-gray-900 dark:text-white"
          placeholder="Motivo de rechazo (requerido si rechaza)"
          value={rejectedReason}
          onChange={(e) => setRejectedReason(e.target.value)}
        />
      )}
      {pendingConfirm ? (
        // Confirmación inline (sin modal, siguiendo el patrón de UI de
        // gestión): reemplaza los botones mientras se decide, en vez de
        // superponer un diálogo.
        <div className="rounded-xl border border-warning-300 bg-warning-50 p-3 dark:border-warning-800 dark:bg-warning-500/10">
          <p className="mb-2 text-sm text-warning-800 dark:text-warning-300">
            ¿Confirmas cambiar el estado a "{LABELS[pendingConfirm]}"? Esta transición se sale del avance habitual del flujo.
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={!!loading}
              onClick={() => {
                const status = pendingConfirm;
                setPendingConfirm(null);
                handleChange(status);
              }}
              className="rounded-lg bg-warning-600 px-3.5 py-2 text-sm font-semibold text-white transition-colors hover:bg-warning-700 disabled:opacity-50"
            >
              Sí, confirmar
            </button>
            <button
              type="button"
              disabled={!!loading}
              onClick={() => setPendingConfirm(null)}
              className="rounded-lg bg-gray-100 px-3.5 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-200 disabled:opacity-50 dark:bg-gray-800 dark:text-gray-300"
            >
              Cancelar
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {nextStatuses.map((status) => (
            <button
              key={status}
              type="button"
              disabled={!!loading}
              onClick={() => handleClickStatus(status)}
              className={`rounded-lg px-3.5 py-2 text-sm font-semibold transition-colors disabled:opacity-50
                ${status === 'rejected'
                  ? 'bg-error-50 text-error-700 hover:bg-error-100 dark:bg-error-500/15 dark:text-error-400'
                  : status === 'active'
                  ? 'bg-success-50 text-success-700 hover:bg-success-100 dark:bg-success-500/15 dark:text-success-400'
                  : status === 'payment_pending'
                  ? 'bg-warning-50 text-warning-700 hover:bg-warning-100 dark:bg-warning-500/15 dark:text-orange-400'
                  : 'bg-brand-50 text-brand-700 hover:bg-brand-100 dark:bg-brand-500/15 dark:text-brand-400'}`}
            >
              {loading === status ? 'Guardando...' : LABELS[status]}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
